import { get, post, put, patch, del } from './api.service';
import { Client, ClientCreateInput, PortfolioSummary, Trade, Payout } from '../types';
import { toTitleCase } from '../utils/formatters';

/**
 * Normalize a client's display-facing name fields to Title Case.
 * Backend data is preserved elsewhere (API payloads); this only affects what
 * the UI renders.
 */
function titleCaseClient<T extends Partial<Client>>(c: T): T {
  return {
    ...c,
    name: c.name ? toTitleCase(c.name) : c.name,
    referredByName: c.referredByName ? toTitleCase(c.referredByName) : c.referredByName,
  } as T;
}

function titleCaseTrade<T extends Partial<Trade>>(t: T): T {
  return {
    ...t,
    clientName: t.clientName ? toTitleCase(t.clientName) : t.clientName,
  } as T;
}

interface ClientsResponse {
  clients: Client[];
}

interface ClientResponse {
  client: Client;
}

interface PortfolioResponse {
  summary: PortfolioSummary;
  openPositions: Trade[];
  recentTrades: Trade[];
  payouts: Payout[];
}

// ============================================================================
// CLIENT SERVICE
// ============================================================================

export const clientService = {
  /**
   * Get all clients
   */
  async getClients(activeOnly = false): Promise<Client[]> {
    const params = activeOnly ? { active: 'true' } : {};
    const response = await get<ClientsResponse>('/clients', params);
    return (response.clients || []).map((c) => titleCaseClient(c as Client));
  },

  /**
   * Get a specific client by ID
   */
  async getClientById(clientId: string): Promise<Client> {
    const response = await get<ClientResponse>(`/clients/${clientId}`);
    return titleCaseClient(response.client);
  },

  /**
   * Create a new client
   */
  async createClient(data: ClientCreateInput): Promise<Client> {
    const response = await post<ClientResponse>('/clients', data);
    return titleCaseClient(response.client);
  },

  /**
   * Update an existing client
   */
  async updateClient(clientId: string, data: Partial<ClientCreateInput>): Promise<Client> {
    const response = await put<ClientResponse>(`/clients/${clientId}`, data);
    return titleCaseClient(response.client);
  },

  /**
   * Toggle client active status
   */
  async toggleClientActive(clientId: string): Promise<Client> {
    const response = await patch<ClientResponse>(`/clients/${clientId}/toggle-active`);
    return titleCaseClient(response.client);
  },

  /**
   * HARD-delete a client and all its linked data (admin/super-admin only). Irreversible.
   */
  async deleteClient(clientId: string): Promise<void> {
    await del(`/clients/${clientId}`);
  },

  /**
   * Add funds (capital top-up) to a client.
   * Writes an audit row + bumps initialCapital + currentDeployedAmount.
   */
  async addFunds(
    clientId: string,
    input: { amount: number; date?: string; notes?: string },
  ): Promise<{ addition: any; updatedClient: Client }> {
    const response = await post<{ addition: any; updatedClient: Client }>(
      `/clients/${clientId}/add-funds`,
      input,
    );
    return {
      addition: response.addition,
      updatedClient: titleCaseClient(response.updatedClient),
    };
  },

  /**
   * Get capital-addition history for a client (newest first).
   */
  async getFundAdditions(clientId: string): Promise<any[]> {
    const response = await get<{ additions: any[] }>(`/clients/${clientId}/fund-additions`);
    return response.additions || [];
  },

  /**
   * Get client portfolio with RABBIT SCORE
   */
  async getClientPortfolio(clientId: string): Promise<PortfolioResponse> {
    const response = await get<PortfolioResponse>(`/clients/${clientId}/portfolio`);
    return {
      ...response,
      openPositions: (response.openPositions || []).map((t) => titleCaseTrade(t)),
      recentTrades: (response.recentTrades || []).map((t) => titleCaseTrade(t)),
    };
  },

  async snapshotDailyCash(): Promise<{ message: string; date: string }> {
    const response = await post<{ message: string; date: string }>('/clients/snapshot-daily-cash', {});
    return response;
  },
};

export default clientService;
